// Styles
import "./Dashboard.scss";

// React Hooks
import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";

// Icons
import FilterIcon from "../../_assets/icons/FilterIcon";

// Components
import FilterModal from "../../components/FilterModal";
import type { FilterState } from "../../components/FilterModal";
import QAFindingsTab from "../../components/QAFindingsTab";
import KeyMetricsSkeleton from "../../components/KeyMetricsSkeleton/KeyMetricsSkeleton";

// Context
import { useDashboard } from "../../context/DashboardContext";
import { useMultipleFileUpload } from "../../context/MultipleFileUploadContext";
import { useAppContext } from "../../context/app-context";

// API
import {
  getKeyMetrics,
  getFindingsSummary,
} from "../../api/data-services/task-api";
// import { getFindingsSummary } from "../../helpers/fileUploadApi";
import { getBrands, getChannels } from "../../api/data-services/dashboard-api";
import { formatDateRange } from "../../helpers/dateUtils";

// UI Components
import {
  Headline3,
  Headline2,
  Label2,
  Subtitle2,
  Icon,
  // ButtonWorkflow,
  ButtonRound,
  ButtonIcon,
} from "@bms-ai/bms-ai-ui-lib/components";
import { useSession } from "../../context/session-context";
import { formatChannelValue } from "../../helpers/helpers";

const Dashboard: React.FC = () => {
  const navigate = useNavigate();
  const { resetAllFiles } = useMultipleFileUpload();
  const { resetAllSelections } = useAppContext();
  const [activeTab, setActiveTab] = useState<string>("QA Findings");

  const [isFilterModalOpen, setIsFilterModalOpen] = useState<boolean>(false);
  const [hasActiveFilters, setHasActiveFilters] = useState<boolean>(false);
  // Separate loader for findings table
  const [isFindingsLoading, setIsFindingsLoading] = useState<boolean>(false);

  // Filter state lifted from FilterModal
  const initialFilters: FilterState = {
    status: "",
    brand: "",
    channel: "",
    contrast: 50,
    searchTaskId: "",
    sortBy: "",
  };
  const [filters, setFilters] = useState<FilterState>(initialFilters);

  // Use Dashboard Context
  const {
    dashboardState,
    setKeyMetrics,
    setFindingsSummary,
    setBrands,
    setChannels,
    setIsLoading,
    setError,
  } = useDashboard();
  const { keyMetrics, findingsSummary, brands, channels, isLoading } =
    dashboardState;

  const { moduleAccessToken } = useSession();

  const tabs = ["QA Findings", "Tasks", "Analytics", "Usage Data"];

  const handleBackToDashHome = () => {
    const platformUrl = import.meta.env.VITE_PLATFORM_CLIENT_URL;
    if (platformUrl) {
      window.location.href = platformUrl;
    } else {
      navigate("/");
    }
  };

  const handleStartNewAnalysis = () => {
    resetAllFiles();
    resetAllSelections();
    navigate("/file-uploads");
  };

  // Fetch brands and channels data
  const fetchBrandAndChannelData = async () => {
    try {
      const [brandsData, channelsData] = await Promise.all([
        getBrands(moduleAccessToken),
        getChannels(moduleAccessToken),
      ]);

      if (brandsData?.data) {
        const filteredBrandsData = {
          ...brandsData.data,
          brands: Array.isArray(brandsData.data.brands)
            ? brandsData.data.brands.filter(
                (brand: any) => !["Opdivo", "Opdualag", "Breyanzi"].includes(brand.brand_name)
              )
            : brandsData.data.brands,
        };
        setBrands(filteredBrandsData);
      }
      if (channelsData?.data) {
        setChannels(channelsData.data);
      }
    } catch (error) {
      console.error("Error fetching brands and channels:", error);
    }
  };

  // Fetch dashboard data on component mount
  useEffect(() => {
    const fetchDashboardData = async () => {
      setIsLoading(true);
      try {
        const [metricsData, findingsData] = await Promise.all([
          getKeyMetrics(undefined, moduleAccessToken),
          getFindingsSummary(undefined, moduleAccessToken),
        ]);

        setKeyMetrics(metricsData);
        setFindingsSummary(findingsData);

        // Fetch brands and channels
      } catch (error) {
        console.error("Error fetching dashboard data:", error);
        setError(
          error instanceof Error
            ? error.message
            : "Failed to fetch dashboard data",
        );
      } finally {
        setIsLoading(false);
      }
    };

    fetchDashboardData();
    fetchBrandAndChannelData();

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleDownloadCSV = async () => {
    if (!findingsSummary || !keyMetrics) return;

    try {
      // Dynamically import xlsx library
      const XLSX = await import('xlsx');

      // Create workbook
      const workbook = XLSX.utils.book_new();

      // Sheet 1: Key Metrics
      const metricsData = [
        ["Metric", "Value"],
        ["Total Files Analyzed", keyMetrics.total_files_analysed],
        ["Average Compliance Score", `${keyMetrics.average_compliance_score.toFixed(1)}%`],
        ["Compliant Files", keyMetrics.compliant_files],
        ["Total Issues Found", keyMetrics.total_issues_found],
      ];
      const metricsSheet = XLSX.utils.aoa_to_sheet(metricsData);
      XLSX.utils.book_append_sheet(workbook, metricsSheet, "Key Metrics");

      // Sheet 2: Findings Summary
      const findingsHeaders = ["Check Name", "Pass", "Fail", "Pass Rate"];
      const findingsRows = findingsSummary.check_wise_count.map((check) => [
        check.check_name,
        check.pass,
        check.fail,
        `${check.pass_rate.toFixed(1)}%`,
      ]);
      const findingsData = [findingsHeaders, ...findingsRows];
      const findingsSheet = XLSX.utils.aoa_to_sheet(findingsData);
      XLSX.utils.book_append_sheet(workbook, findingsSheet, "QA Findings");

      // Generate Excel file and download
      XLSX.writeFile(workbook, "qa-dashboard-report.xlsx");
    } catch (error) {
      console.error("Error generating Excel file:", error);
      alert("Failed to generate report. Please try again.");
    }
  };

  const handleOpenFilterModal = () => {
    setIsFilterModalOpen(true);
  };

  const handleCloseFilterModal = () => {
    setIsFilterModalOpen(false);
  };

  const handleApplyFilters = async (appliedFilters: FilterState) => {
    setFilters(appliedFilters);
    const params: {
      brand?: string[];
      channel?: string[];
      date_range?: string;
    } = {};
    if (appliedFilters.brand) {
      params.brand = [appliedFilters.brand];
    }
    if (appliedFilters.channel) {
      params.channel = [formatChannelValue(appliedFilters.channel)];
    }
    // Only add date_range if both startDate and endDate are present
    if (
      appliedFilters.dateRange?.startDate &&
      appliedFilters.dateRange?.endDate
    ) {
      params.date_range = formatDateRange(
        appliedFilters.dateRange.startDate,
        appliedFilters.dateRange.endDate,
      );
    }

    // Show loaders for both metrics and findings
    setIsLoading(true);
    setIsFindingsLoading(true);
    try {
      const [metricsData, findingsData] = await Promise.all([
        getKeyMetrics(params, moduleAccessToken),
        getFindingsSummary(params, moduleAccessToken),
      ]);
      setKeyMetrics(metricsData);
      setFindingsSummary(findingsData);

      const filtersActive =
        Boolean(appliedFilters.brand) ||
        Boolean(appliedFilters.channel) ||
        Boolean(
          appliedFilters.dateRange?.startDate &&
          appliedFilters.dateRange?.endDate,
        );
      setHasActiveFilters(filtersActive);
    } catch (error) {
      console.error("Error applying filters:", error);
      setError(
        error instanceof Error ? error.message : "Failed to apply filters",
      );
    } finally {
      setIsLoading(false);
      setIsFindingsLoading(false);
    }
  };

  const handleClearFilters = async () => {
    setHasActiveFilters(false);
    setFilters(initialFilters);
    setIsLoading(true);
    setIsFindingsLoading(true);
    try {
      const [metricsData, findingsData] = await Promise.all([
        getKeyMetrics(undefined, moduleAccessToken),
        getFindingsSummary(undefined, moduleAccessToken),
      ]);
      setKeyMetrics(metricsData);
      setFindingsSummary(findingsData);
    } catch (error) {
      console.error("Error fetching data after clearing filters:", error);
      setError(error instanceof Error ? error.message : "Failed to fetch data");
    } finally {
      setIsLoading(false);
      setIsFindingsLoading(false);
    }
  };

  return (
    <div id="content-qa-dashboard-root" className="content-qa-isolated">
      <div className="dashboard-container">
        {/* Header Section */}
        <div className="dashboard-header">
          <button className="back-link" onClick={handleBackToDashHome}>
            <Icon type="arrow-left" size="small" />
            <span>Back to Oasis Home</span>
          </button>
        </div>

        <div className="dashboard-content">
          <div className="header-title-section">
            <Headline2 className="dashboard-title font-bold">
              Content QA Dashboard
            </Headline2>
            <ButtonRound
              className="start-analysis-btn"
              children="Start new analysis"
              onClick={handleStartNewAnalysis}
              disabled={!brands || !channels}
            />
          </div>

          {/* Key Metrics Section */}
          <div className="dashboard-section">
            <div className="section-header">
              <Headline3 className="section-title font-bold">
                Key metrics
              </Headline3>
              <div className="section-actions">
                {hasActiveFilters && (
                  <ButtonIcon
                    className="clear-filters-link"
                    onClick={handleClearFilters}
                    iconType="delete"
                    iconFirst
                    label={"Clear filters"}
                  >
                    Clear filters
                  </ButtonIcon>
                )}
                <button
                  className="action-btn filter-btn"
                  onClick={handleOpenFilterModal}
                  disabled={!brands || !channels}
                >
                  <FilterIcon />
                </button>
                <button
                  className="action-link download-link"
                  onClick={handleDownloadCSV}
                  disabled={isLoading || !findingsSummary || !keyMetrics}
                >
                  Download report in Excel
                </button>
              </div>
            </div>

            <div className="metrics-grid">
              {/* Only show metrics loader when the whole dashboard is loading, not when filtering findings */}
              {isLoading ? (
                <KeyMetricsSkeleton />
              ) : (
                <>
                  <div className="metric-card">
                    <Label2 className="metric-label font-semibold">
                      Total Files Analyzed
                    </Label2>
                    <Headline2 className="metric-value font-bold">
                      {keyMetrics?.total_files_analysed ?? 0}
                    </Headline2>
                  </div>
                  <div className="metric-card">
                    <Label2 className="metric-label font-semibold">
                      Average Compliance Score
                    </Label2>
                    <Headline2 className="metric-value font-bold">
                      {`${keyMetrics?.average_compliance_score?.toFixed(1) ?? 0}%`}
                    </Headline2>
                  </div>
                  <div className="metric-card">
                    <Label2 className="metric-label font-semibold">
                      Compliant Files
                    </Label2>
                    <Headline2 className="metric-value font-bold">
                      {keyMetrics?.compliant_files ?? 0}
                    </Headline2>
                  </div>
                  <div className="metric-card">
                    <Label2 className="metric-label font-semibold">
                      Total Issues found
                    </Label2>
                    <Headline2 className="metric-value font-bold">
                      {keyMetrics?.total_issues_found ?? 0}
                    </Headline2>
                  </div>
                </>
              )}
            </div>
          </div>

          {/* My QA Assets Section */}
          <div className="section-header">
            <Headline3 className="section-title font-bold">
              My QA assets
            </Headline3>
          </div>

          <div className="qa-assets-section">
            {/* Tabs */}
            <div className="dashboard-tabs">
              {tabs.map((tab) => (
                <div
                  key={tab}
                  className={`dashboard-tab ${activeTab === tab ? "active" : ""}`}
                  onClick={() => setActiveTab(tab)}
                >
                  <Label2>{tab}</Label2>
                </div>
              ))}
            </div>

            {/* QA Findings Tab */}
            {activeTab === "QA Findings" && (
              <QAFindingsTab
                findingsSummary={findingsSummary}
                isFindingsLoading={isFindingsLoading}
              />
            )}

            {/* Placeholder for other tabs */}
            {activeTab !== "QA Findings" && (
              <div className="tab-placeholder">
                <Subtitle2>{activeTab} view coming soon...</Subtitle2>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Filter Modal */}
      <FilterModal
        isOpen={isFilterModalOpen}
        onClose={handleCloseFilterModal}
        onApplyFilters={handleApplyFilters}
        brands={brands}
        channels={channels}
        filters={filters}
        setFilters={setFilters}
      />
    </div>
  );
};

export default Dashboard;
